import { useState } from "react";
import { accountService, taskService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState, useEdit, useRouter } from "@miplatform/hooks";
import { toastService } from "@miplatform/toast";
import { useGlobalManagerApproval } from "@mibuildweb/components";

export const useReconcileViewModel = ({ orderId }) => {
  const [order, setOrder] = useState(null);
  const [orderTemplates, setOrderTemplates] = useState([]);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [invoicePdfs, setInvoicePdfs] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const [variances, setVariances] = useState([]);
  const stateManager = useComponentState();
  const pdfManager = useComponentState();
  const isLoading = stateManager.isBusy();
  const notesEdit = useEdit({
    notes: order?.notes || "",
    varianceNotes: order?.varianceNotes || "",
  });
  const { navigate } = useRouter();
  const { executeApiRequest } = useGlobalManagerApproval();

  const getOrder = () => {
    return stateManager.run(async () => {
      const response = await taskService.order.getById({ orderId });
      if (response.status === HTTP_STATUSES.OK) {
        setOrder(response.data);
        return response.data;
      }
      return null;
    });
  };

  const updateOrder = async ({ data }) => {
    return stateManager.run(async () => {
      const response = await taskService.order.update({
        orderId,
        data,
      });
      return response.status === HTTP_STATUSES.OK;
    });
  };

  const getOrderTemplates = () => {
    stateManager.run(async () => {
      const response = await taskService.orderTemplates.getAll({
        sort: "name",
      });
      if (response.status === HTTP_STATUSES.OK) {
        setOrderTemplates(
          response.data.map((orderType) => ({
            ...orderType,
            label: orderType.name,
            value: orderType.id,
          }))
        );
      }
    });
  };

  const getPurchaseOrders = () => {
    stateManager.run(async () => {
      const response = await accountService.billingCommitments.getAll({
        filters: {
          homeId: {
            eq: { type: "guid", value: order?.homeId },
          },
        },
        params: {
          includeDraftInvoices: true,
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        setPurchaseOrders(response.data);
      }
    });
  };

  const getInvoices = async ({ orderId }) => {
    return stateManager.run(async () => {
      const response = await accountService.invoices.getAll({
        filters: {
          orderId: {
            eq: { type: "guid", value: orderId },
          },
        },
        sort: "createdOn",
      });
      return response.status === HTTP_STATUSES.OK ? response.data : null;
    });
  };

  const createInvoice = async ({ data }) => {
    return stateManager.run(async () => {
      const response = await accountService.invoices.create({
        data,
      });
      return response.status === HTTP_STATUSES.CREATED ? response.data : null;
    });
  };

  const createInvoiceItem = async ({ data }) => {
    return stateManager.run(async () => {
      const response = await accountService.invoiceItems.create({
        data,
        toastMessageConfig: {
          success: {
            text: "Invoice item created successfully.",
          },
        },
      });

      return response.status === HTTP_STATUSES.CREATED ? response.data : null;
    });
  };

  const updateInvoiceItem = async ({ id, data }) => {
    return stateManager.run(async () => {
      const { status, data: updatedData } =
        await accountService.invoiceItems.update({
          id,
          data,
          toastMessageConfig: {
            success: {
              text: "Invoice item updated successfully.",
            },
          },
        });

      return status === HTTP_STATUSES.OK ? updatedData : null;
    });
  };

  const deleteInvoiceItem = async ({ id, modifiedOn }) => {
    return stateManager.run(async () => {
      const { status } = await accountService.invoiceItems.delete({
        id,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Invoice item deleted successfully.",
          },
        },
      });
      return status === HTTP_STATUSES.OK;
    });
  };

  const fetchAndEnsureDraftInvoice = async () => {
    return stateManager.run(async () => {
      if (!order?.id || !order?.orderTemplateName) {
        setInvoices([]);
        return;
      }

      const fetchedInvoices = (await getInvoices({ orderId: order.id })) || [];

      if (!fetchedInvoices.length) {
        const newDraftInvoice = await createInvoice({
          data: {
            orderId: order.id,
            name: order.orderTemplateName,
          },
        });
        if (newDraftInvoice) {
          fetchedInvoices.push(newDraftInvoice);
        }
      }

      setInvoices((prev) => {
        const tempInvoices = prev.filter(
          (invoice) => invoice.isNew && !invoice.id
        );
        return [...fetchedInvoices, ...tempInvoices];
      });
    });
  };

  const handleFinalizeInvoice = async ({
    invoiceDate,
    invoiceIdsToExcludeFromQuickBooks,
  }) => {
    return stateManager.run(async () => {
      if (!order?.id) {
        return;
      }
      const options = await taskService.order.getFinalizeInvoiceOfOrderOptions({
        orderId: order.id,
        data: {
          invoiceDate,
          modifiedOn: order.modifiedOn,
          invoiceIdsToExcludeFromQuickBooks,
        },
      });

      const callback = async (response, cb) => {
        if (notesEdit.isDirty()) {
          const data = await getOrder();
          await updateOrder({
            data: {
              ...notesEdit.getValue(),
              modifiedOn: data.modifiedOn,
            },
          });
        }

        if (response.status === HTTP_STATUSES.OK) {
          if (cb) cb();
          toastService.success({
            text: "Order invoiced successfully.",
          });
          navigate("/accounting/invoices/pending-invoices");
        }
      };

      await executeApiRequest({
        options,
        noInitialErrorMessage: true,
        callback,
      });
    });
  };

  const handlePreBillOrderWithManagerApproval = async () => {
    return stateManager.run(async () => {
      if (!order?.id) {
        return;
      }

      const options = await taskService.order.preBillInvoicesOptions({
        orderId: order.id,
        modifiedOn: order.modifiedOn,
      });

      const callback = async (response, cb) => {
        if (notesEdit.isDirty()) {
          const data = await getOrder();
          await updateOrder({
            data: {
              ...notesEdit.getValue(),
              modifiedOn: data.modifiedOn,
            },
          });
        }

        if (response.status === HTTP_STATUSES.NO_CONTENT) {
          if (cb) cb();
          toastService.success({
            text: "Order prebilled successfully.",
          });
          setTimeout(() => {
            navigate("/accounting/invoices/pre-bill");
          }, 200);
        }
      };

      await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Prebilling order with variance requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });
    });
  };

  const handlePreBillOrder = async () => {
    return stateManager.run(async () => {
      if (!order?.id) {
        return;
      }

      const response = await taskService.order.preBillInvoices({
        orderId: order.id,
        modifiedOn: order.modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Order prebilled successfully.",
          },
        },
      });

      if (notesEdit.isDirty()) {
        const data = await getOrder();

        await updateOrder({
          data: {
            ...notesEdit.getValue(),
            modifiedOn: data.modifiedOn,
          },
        });
      }

      if (response.status === HTTP_STATUSES.NO_CONTENT) {
        setTimeout(() => {
          navigate("/accounting/invoices/pre-bill");
        }, 200);
      }
    });
  };

  const getOrderInvoicePdfs = async () => {
    return pdfManager.run(async () => {
      if (!order?.id) {
        return null;
      }

      const response = await accountService.invoices.getInvoicePdfs({
        orderId: order.id,
      });

      if (response.status === HTTP_STATUSES.OK) {
        setInvoicePdfs(response.data);
        return response.data;
      }
      return null;
    });
  };

  const formatVarianceDisplay = (variance) => {
    if (variance == null) return "";

    const value = parseFloat(variance);
    const formattedValue = Math.abs(value).toFixed(2);

    if (value < 0) {
      return `($${formattedValue})`;
    }

    return `$${formattedValue}`;
  };

  const getOrderVariances = async () => {
    return stateManager.run(async () => {
      const response = await accountService.variances.getOrderVariances({
        orderId,
      });
      if (response.status === HTTP_STATUSES.OK) {
        const groupedArray = Object.values(
          response.data.reduce((acc, item) => {
            const key = item.originatingInvoice?.id;
            if (!key) return acc;

            if (!acc[key]) {
              acc[key] = {
                originatingInvoice: item.originatingInvoice,
                varianceItems: [],
              };
            }
            const filteredPayingItems = item.payingInvoiceItem.filter(
              (pi) => pi.orderId === orderId
            );

            acc[key].varianceItems.push({
              ...item,
              payingInvoiceItem: filteredPayingItems,
              name: item.originatingInvoice?.name,
              unitPrice: filteredPayingItems[0]?.totalPrice,
              invoiceId: filteredPayingItems[0]?.invoiceId,
            });
            return acc;
          }, {})
        );
        setVariances(groupedArray);
      }
      return null;
    });
  };

  const checkInvoiceVoidable = async ({ id }) => {
    return stateManager.run(async () => {
      const response = await accountService.invoices.checkInvoiceVoidable({
        id,
      });
      return response.status === HTTP_STATUSES.OK ? response.data : [];
    });
  };

  const voidInvoice = async ({ id, modifiedOn }) => {
    return stateManager.run(async () => {
      const options = await accountService.invoices.getVoidInvoiceOptions({
        id,
        modifiedOn,
        toastMessageConfig: {
          success: {
            text: "Invoice voided successfully.",
          },
        },
      });

      const callback = async (response, cb) => {
        if (response.status === HTTP_STATUSES.OK) {
          if (cb) cb();
          toastService.success({
            text: "Invoice voided successfully.",
          });
        }
      };

      await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Voiding invoice requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });
    });
  };
  const addPaymentToInvoice = async ({ invoiceId, data }) => {
    return stateManager.run(async () => {
      const options = await accountService.invoices.getAddPaymentOptions({
        id: invoiceId,
        data,
      });

      const callback = async (response, cb) => {
        if (response.status === HTTP_STATUSES.CREATED) {
          if (cb) cb();
          toastService.success({
            text: "Payment added successfully.",
          });
          await fetchAndEnsureDraftInvoice();
        }
      };

      const response = await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Adding payment requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });

      return response.status === HTTP_STATUSES.CREATED ? response.data : null;
    });
  };
  const updatePaymentFromInvoice = async ({ invoiceId, data }) => {
    return stateManager.run(async () => {
      const options = await accountService.invoices.getUpdatePaymentOptions({
        id: invoiceId,
        data,
      });

      const callback = async (response, cb) => {
        if (response.status === HTTP_STATUSES.OK) {
          if (cb) cb();
          toastService.success({
            text: "Payment updated successfully.",
          });
          await fetchAndEnsureDraftInvoice();
        }
      };

      const response = await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Updating payment requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });

      return response.status === HTTP_STATUSES.OK;
    });
  };
  const removePaymentFromInvoice = async ({ invoiceId, data }) => {
    return stateManager.run(async () => {
      const options = await accountService.invoices.getDeletePaymentOptions({
        id: invoiceId,
        data,
      });

      const callback = async (response, cb) => {
        if (response.status === HTTP_STATUSES.OK) {
          if (cb) cb();
          toastService.success({
            text: "Payment removed successfully.",
          });
          await fetchAndEnsureDraftInvoice();
        }
      };

      const response = await executeApiRequest({
        options,
        callback,
        toastMessageForManagerApproval: {
          text: `Removing payment requires manager approval.`,
          subtext: `Please provide your username and PIN to proceed.`,
        },
      });

      return response.status === HTTP_STATUSES.OK;
    });
  };

  const handleSaveNotes = async () => {
    return stateManager.run(async () => {
      if (!order?.id || !notesEdit.isDirty()) {
        return;
      }

      const success = await updateOrder({
        data: {
          ...notesEdit.getValue(),
          modifiedOn: order.modifiedOn,
        },
      });

      if (success) {
        toastService.success({
          text: "Notes saved successfully.",
        });

        await getOrder();
        notesEdit.reset();
      }
    });
  };

  return {
    order,
    orderTemplates,
    purchaseOrders,
    isLoading,
    isPdfLoading: pdfManager.isBusy(),
    invoicesModule: {
      getAll: getInvoices,
      create: createInvoice,
      createItem: createInvoiceItem,
      updateItem: updateInvoiceItem,
      deleteItem: deleteInvoiceItem,
      update: accountService.invoices.update,
      delete: accountService.invoices.delete,
      handleFinalizeInvoice,
      handlePreBillOrder,
      handlePreBillOrderWithManagerApproval,
      getOrderInvoicePdfs,
      getOrderVariances,
      voidInvoice,
      checkInvoiceVoidable,
      addPaymentToInvoice,
      removePaymentFromInvoice,
      updatePaymentFromInvoice,
      handleSaveNotes,
    },
    getOrder,
    getOrderTemplates,
    getPurchaseOrders,
    setPurchaseOrders,
    invoices,
    setInvoices,
    variances,
    setVariances,
    invoicePdfs,
    setInvoicePdfs,
    fetchAndEnsureDraftInvoice,
    notesEdit,
    formatVarianceDisplay,
  };
};
